
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipeService } from '../../services/api/recipes';
import { Recipe, Ingredient, PreparationStep } from '../../types/recipe';
import { useRestaurant } from '@/contexts/RestaurantContext';
import { RecipeHeader, RecipeIngredients } from './components';

import {
  ArrowLeft,
  DollarSign,
  FileText,
  History,
  Edit,
  Save,
  X,
  Trash2,
  Plus,
  LineChart,
  Clock,
  Bookmark
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RecipeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { restaurantProfile, isLoading: isLoadingProfile } = useRestaurant();
  const queryClient = useQueryClient();
  
  // Get return URL from parameters, default to /recipes
  const returnUrl = searchParams.get('returnUrl') ? decodeURIComponent(searchParams.get('returnUrl')!) : '/recipes';

  // Consolidated state management
  const [editState, setEditState] = useState({
    isEditing: false,
    editingSteps: false,
    editingIngredients: false
  });
  const [recipeState, setRecipeState] = useState({
    editedRecipe: null as Recipe | null,
    ingredients: [] as Ingredient[],
    preparationSteps: [] as PreparationStep[],
    newStep: { id: "", instruction: "" } as PreparationStep,
    newIngredient: { id: 0, name: "", amount: 0, metric: { id: 0, name: "" }, optional: false } as Ingredient
  });

  // Validate recipe ID format
  const recipeId = id ? parseInt(id, 10) : null;
  const isValidRecipeId = recipeId && !isNaN(recipeId);

  // Debug logs for query conditions
  useEffect(() => {
    console.log('[RecipeDetail] Query conditions:', {
      restaurantProfile,
      businessUnitId: restaurantProfile?.business_unit_id,
      recipeId,
      isValidRecipeId,
      queryEnabled: Boolean(restaurantProfile?.business_unit_id && isValidRecipeId)
    });
  }, [restaurantProfile, recipeId, isValidRecipeId]);

  const { data: recipeData, isLoading, error: queryError } = useQuery({
    queryKey: ['recipe', recipeId, restaurantProfile?.business_unit_id],
    enabled: Boolean(restaurantProfile?.business_unit_id && isValidRecipeId),
    queryFn: async () => {
      const businessUnitId = restaurantProfile?.business_unit_id;
      
      if (!isValidRecipeId || !businessUnitId) {
        console.error('[RecipeDetail] Invalid parameters:', { recipeId, businessUnitId });
        throw new Error('Invalid recipe ID or business unit ID');
      }
      
      try {
        const numericId = Number(id);
        if (isNaN(numericId)) {
          console.error('[RecipeDetail] Invalid recipe ID format:', id);
          throw new Error('Invalid recipe ID format');
        }
        console.log('[RecipeDetail] Fetching recipe:', { numericId, businessUnitId });
        const response = await recipeService.getRecipeWithIngredients(numericId, businessUnitId);
        console.log('[RecipeDetail] API Response:', response);
        
        if (!response.recipes.length) {
          console.warn('[RecipeDetail] No recipe found in response');
          return null;
        }
        
        const recipeDetails = response.recipes[0];
        console.log('[RecipeDetail] Processing recipe details:', recipeDetails);
        
        const mappedRecipe = {
          id: recipeDetails.recipe_id,
          name: recipeDetails.recipe_name,
          description: recipeDetails.recipe_description,
          price: recipeDetails.price,
          cost: recipeDetails.cost,
          prep_time: recipeDetails.prep_time,
          profit_margin: recipeDetails.profit_margin,
          ingredients: recipeDetails.ingredients.map(ing => {
            console.log('[RecipeDetail] Processing ingredient:', ing);
            return {
              id: ing.id.toString(),
              name: ing.name,
              quantity: ing.amount.toString(),
              unit: ing.metric.name,
              cost: ing.cost ? ing.cost.toString() : "0"
            };
          }),
          preparationSteps: recipeDetails.preparation_steps?.map(step => ({
            id: step.id.toString(),
            instruction: step.instruction
          })) || [],
          history: []
        } as Recipe;
        
        console.log('[RecipeDetail] Mapped recipe data:', mappedRecipe);
        return mappedRecipe;
      } catch (error) {
        console.error('[RecipeDetail] Error fetching recipe:', error);
        throw error;
      }
    }
  });

  // Initialize recipe state when data is loaded
  useEffect(() => {
    if (recipeData) {
      console.log('[RecipeDetail] Updating recipe state with new data:', recipeData);
      setRecipeState(prev => ({
        ...prev,
        editedRecipe: recipeData,
        ingredients: recipeData.ingredients,
        preparationSteps: recipeData.preparationSteps || []
      }));
    }
  }, [recipeData]);

  const { mutate: updateRecipe } = useMutation({
    mutationFn: (recipe: Recipe) => recipeService.updateRecipe(recipe),
    onSuccess: () => {
      // Invalidate both the recipe detail and list queries
      queryClient.invalidateQueries({ 
        queryKey: ['recipe', recipeId, restaurantProfile?.business_unit_id]
      });
      queryClient.invalidateQueries({
        queryKey: ['recipes', restaurantProfile?.business_unit_id]
      });
      setEditState(prev => ({ ...prev, isEditing: false }));
    }
  });

  // Consolidated handlers
  const handlers = {
    handleEdit: () => setEditState(prev => ({ ...prev, isEditing: true })),
    handleCancel: () => {
      setEditState(prev => ({ ...prev, isEditing: false }));
      if (recipeData) {
        setRecipeState(prev => ({
          ...prev,
          editedRecipe: recipeData
        }));
      }
    },
    handleSave: () => {
      if (recipeState.editedRecipe) {
        updateRecipe(recipeState.editedRecipe);
      }
    }
  };

  // Calculate total ingredient cost
  const calculateTotalIngredientCost = () => {
    if (!recipeState.ingredients || recipeState.ingredients.length === 0) {
      return 0;
    }
    
    return recipeState.ingredients.reduce((total, ing) => {
      const cost = parseFloat(ing.cost) || 0;
      return total + cost;
    }, 0).toFixed(2);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (queryError) {
    return <div>Error loading recipe</div>;
  }

  if (!recipeData) {
    return <div>Recipe not found</div>;
  }

  // Early returns need to be after hooks
  if (isLoadingProfile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading restaurant profile...</div>
      </div>
    );
  }

  if (!restaurantProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-lg">Please set up your restaurant profile first</p>
        <Button onClick={() => navigate('/settings')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go to Settings
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading recipe details...</div>
      </div>
    );
  }

  if (!recipeData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-lg">Recipe not found</p>
        <Button onClick={() => navigate(returnUrl)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Recipes
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate(returnUrl)}
          className="mb-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-3xl font-semibold tracking-tight">{recipeData.name}</h1>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="mb-4 bg-secondary">
          <TabsTrigger value="details" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <FileText className="h-4 w-4" />
            Recipe Details
          </TabsTrigger>
          <TabsTrigger value="cost" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <DollarSign className="h-4 w-4" />
            Cost Analysis
          </TabsTrigger>
        </TabsList>

        {/* Recipe Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RecipeHeader
              recipe={recipeData}
              isEditing={editState.isEditing}
              editedRecipe={recipeState.editedRecipe}
              onEdit={handlers.handleEdit}
              onSave={handlers.handleSave}
              onCancel={handlers.handleCancel}
              onRecipeChange={(updatedRecipe) => setRecipeState(prev => ({
                ...prev,
                editedRecipe: updatedRecipe
              }))}
            />
            
            <RecipeIngredients
              ingredients={recipeState.ingredients}
              isEditing={editState.editingIngredients}
              onEdit={() => setEditState(prev => ({ ...prev, editingIngredients: true }))}
              onSave={() => setEditState(prev => ({ ...prev, editingIngredients: false }))}
              onCancel={() => setEditState(prev => ({ ...prev, editingIngredients: false }))}
              onIngredientChange={(updatedIngredients) => setRecipeState(prev => ({
                ...prev,
                ingredients: updatedIngredients
              }))}
            />
            
            <Card className="lg:col-span-3">
              <CardHeader className="bg-secondary/30 border-b">
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Preparation Steps
                  </CardTitle>
                  {!editState.editingSteps ? (
                    <Button onClick={() => setEditState(prev => ({ ...prev, editingSteps: true }))} variant="outline" size="sm" className="bg-card text-foreground">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Steps
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button onClick={() => setEditState(prev => ({ ...prev, editingSteps: false }))} size="sm">
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                      <Button onClick={() => setEditState(prev => ({ ...prev, editingSteps: false }))} variant="outline" size="sm">
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6 bg-card text-foreground">
                <div className="space-y-4">
                  {!editState.editingSteps ? (
                    <ol className="list-decimal space-y-3 ml-5">
                      {recipeState.preparationSteps.map((step) => (
                        <li key={step.id} className="pl-2 pb-2 border-b border-border text-foreground">{step.instruction}</li>
                      ))}
                    </ol>
                  ) : (
                    <div className="space-y-4">
                      {recipeState.preparationSteps.map((step, index) => (
                        <div key={step.id} className="flex gap-2 items-center p-2 rounded-md border border-border bg-muted/30">
                          <Input
                            value={step.instruction}
                            onChange={(e) => {
                              const newSteps = [...recipeState.preparationSteps];
                              newSteps[index] = { ...step, instruction: e.target.value };
                              setRecipeState(prev => ({ ...prev, preparationSteps: newSteps }));
                            }}
                            placeholder="Step instruction"
                            className="flex-1 border-border bg-background text-foreground"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRecipeState(prev => ({
                                ...prev,
                                preparationSteps: prev.preparationSteps.filter(s => s.id !== step.id)
                              }));
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      
                      <div className="flex gap-2 items-center p-2 rounded-md border border-border bg-primary/10">
                        <Input
                          value={recipeState.newStep.instruction}
                          onChange={(e) => setRecipeState(prev => ({
                            ...prev,
                            newStep: { ...prev.newStep, instruction: e.target.value }
                          }))}
                          placeholder="New step instruction"
                          className="flex-1 border-border bg-background text-foreground"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (recipeState.newStep.instruction) {
                              setRecipeState(prev => ({
                                ...prev,
                                preparationSteps: [...prev.preparationSteps, {
                                  id: Date.now().toString(),
                                  instruction: prev.newStep.instruction
                                }],
                                newStep: { id: "", instruction: "" }
                              }));
                            }
                          }}
                          className="text-green-600 hover:text-green-800 hover:bg-green-50 p-1 h-auto"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cost Analysis Tab */}
        <TabsContent value="cost" className="space-y-4">
          <Card>
            <CardHeader className="bg-secondary/30 border-b">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Cost Breakdown
              </CardTitle>
              <CardDescription>
                Detailed analysis of recipe costs and profitability
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 bg-card text-foreground">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card className="border-primary/20 shadow bg-card">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="bg-primary/10 p-3 rounded-full mx-auto mb-2 w-14 h-14 flex items-center justify-center">
                        <DollarSign className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium text-foreground">Total Cost</h3>
                      <p className="text-3xl font-bold text-foreground">${recipeData.cost || calculateTotalIngredientCost()}</p>
                      <p className="text-sm text-muted-foreground">Per serving</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-primary/20 shadow bg-card">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="bg-primary/10 p-3 rounded-full mx-auto mb-2 w-14 h-14 flex items-center justify-center">
                        <LineChart className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium text-foreground">Profit Margin</h3>
                      <p className="text-3xl font-bold text-foreground">{recipeData.profit_margin || '0'}%</p>
                      <p className="text-sm text-muted-foreground">Current</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-primary/20 shadow bg-card">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="bg-primary/10 p-3 rounded-full mx-auto mb-2 w-14 h-14 flex items-center justify-center">
                        <Clock className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium text-foreground">Prep Time</h3>
                      <p className="text-3xl font-bold text-foreground">{recipeData.prep_time || '45m'}</p>
                      <p className="text-sm text-muted-foreground">Labor cost included</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Ingredient Cost Breakdown */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-4 text-foreground">Ingredient Costs</h3>
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead className="text-foreground">Ingredient</TableHead>
                      <TableHead className="text-foreground">Quantity</TableHead>
                      <TableHead className="text-foreground">Unit</TableHead>
                      <TableHead className="text-foreground">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipeState.ingredients.map((ingredient) => (
                      <TableRow key={ingredient.id} className="hover:bg-muted/50 border-border">
                        <TableCell className="font-medium text-foreground">{ingredient.name}</TableCell>
                        <TableCell className="text-foreground">{ingredient.quantity}</TableCell>
                        <TableCell className="text-foreground">{ingredient.unit}</TableCell>
                        <TableCell className="text-foreground">${ingredient.cost}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted font-medium border-border">
                      <TableCell colSpan={3} className="text-right text-foreground">Total Ingredient Cost:</TableCell>
                      <TableCell className="text-foreground">${calculateTotalIngredientCost()}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              
              {/* Price Analysis */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-4 text-foreground">Price Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-card rounded-lg border border-border">
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Selling Price:</span>
                      <span className="font-medium text-foreground">${recipeData.price || '0.00'}</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Food Cost:</span>
                      <span className="font-medium text-foreground">${recipeData.cost || calculateTotalIngredientCost()}</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Estimated Labor:</span>
                      <span className="font-medium text-foreground">$1.50</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Overhead:</span>
                      <span className="font-medium text-foreground">$0.75</span>
                    </div>
                    <div className="flex justify-between font-medium pt-2 border-t border-border">
                      <span className="text-foreground">Profit per Unit:</span>
                      <span className="text-green-400">${(recipeData.price || 0) - (recipeData.cost || parseFloat(calculateTotalIngredientCost())) - 2.25}</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-card rounded-lg border border-border">
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Food Cost Percentage:</span>
                      <span className="font-medium text-foreground">{recipeData.price ? ((recipeData.cost || parseFloat(calculateTotalIngredientCost())) / recipeData.price * 100).toFixed(1) : '0'}%</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Contribution Margin:</span>
                      <span className="font-medium text-foreground">${recipeData.price ? (recipeData.price - (recipeData.cost || parseFloat(calculateTotalIngredientCost()))).toFixed(2) : '0.00'}</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-muted-foreground">Break-even Point:</span>
                      <span className="font-medium text-foreground">50 units</span>
                    </div>
                    <div className="flex justify-between font-medium pt-2 border-t border-border">
                      <span className="text-foreground">Recommended Price:</span>
                      <span className="text-blue-400">${((recipeData.cost || parseFloat(calculateTotalIngredientCost())) * 3.5).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default RecipeDetail;

